import React, { useState, useEffect } from 'react';
import { Checkbox, FormGroup, FormControlLabel, Stack } from '@mui/material';
import DropDown from '../DropDown/DropDown';
import AddressFields from './AddressFields2';
import PropTypes from 'prop-types';

const addressFieldsVariantTypes = {
  all: 'all',
  billingOnly: 'billingOnly',
  shippingOnly: 'shippingOnly',
};

const fieldAlignmentTypes = {
  column: 'column',
  row: 'row',
};

function AddressFieldsDropDown(props) {
  const {
    type = addressFieldsVariantTypes.all,
    billingAddressFields = {},
    shippingAddressFields = {},
    billingAddressSettings = {},
    shippingAddressSettings = {},
    setBillingAddressFields,
    setShippingAddressFields,
    billingFormValidationRef,
    shippingFormValidationRef,
    disableDropdownExpansion = false,
    fieldAlignment = fieldAlignmentTypes.column,
  } = props;
  const [isShippingSameAsBilling, setIsShippingSameAsBilling] = useState(false);
  const [isShippingEnabled, setIsShippingEnabled] = useState(false);
  const [isBillingEnabled, setIsBillingEnabled] = useState(false);

  const handleChangeCheckbox = () => {
    if (!isShippingSameAsBilling) {
      // new check state
      setShippingAddressFields(billingAddressFields);
    }
    setIsShippingSameAsBilling(!isShippingSameAsBilling);
  };

  useEffect(() => {
    let isBillingEnabledTemp = false;
    Object.values(billingAddressSettings)?.forEach((value) => {
      isBillingEnabledTemp = isBillingEnabledTemp || value;
    });

    if (![addressFieldsVariantTypes.all, addressFieldsVariantTypes.billingOnly].includes(type)) {
      isBillingEnabledTemp = false;
    }

    let isShippingEnabledTemp = false;
    Object.values(shippingAddressSettings)?.forEach((value) => {
      isShippingEnabledTemp = isShippingEnabledTemp || value;
    });

    if (![addressFieldsVariantTypes.all, addressFieldsVariantTypes.shippingOnly].includes(type)) {
      isShippingEnabledTemp = false;
    }

    setIsBillingEnabled(isBillingEnabledTemp);
    setIsShippingEnabled(isShippingEnabledTemp);
  }, [type, billingAddressSettings, shippingAddressSettings]);

  if (!isBillingEnabled && !isShippingEnabled) {
    return null;
  }

  return (
    <Stack>
      {isBillingEnabled && (
        <DropDown
          id="billingAddress"
          title="Billing Address"
          defaultExpanded={true} // Default to open dropdown
          disableExpand={disableDropdownExpansion}
        >
          <Stack spacing={2}>
            <div>
              <AddressFields
                fieldAlignment={fieldAlignment}
                addressFields={billingAddressFields}
                addressSettings={billingAddressSettings}
                isAddressRequired={billingAddressSettings?.isBillingAddressRequired}
                setAddressFields={setBillingAddressFields}
                ref={billingFormValidationRef}
              />
            </div>
            {type === addressFieldsVariantTypes.all && isShippingEnabled && (
              <FormGroup>
                <FormControlLabel
                  id="shipping-checkbox-label"
                  control={<Checkbox checked={isShippingSameAsBilling} onChange={handleChangeCheckbox} />}
                  label="Shipping same as Billing Address"
                />
              </FormGroup>
            )}
          </Stack>
        </DropDown>
      )}
      {isShippingEnabled && (
        <DropDown
          id="shippingAddress"
          title="Shipping Address"
          defaultExpanded={disableDropdownExpansion}
          disableExpand={disableDropdownExpansion}
        >
          <AddressFields
            fieldAlignment={fieldAlignment}
            addressFields={shippingAddressFields}
            addressSettings={shippingAddressSettings}
            isAddressRequired={shippingAddressSettings?.isShippingAddressRequired}
            setAddressFields={setShippingAddressFields}
            ref={shippingFormValidationRef}
          />
        </DropDown>
      )}
    </Stack>
  );
}

AddressFieldsDropDown.propTypes = {
  type: PropTypes.string,
  billingAddressFields: PropTypes.shape({
    addressOne: PropTypes.string,
    addressTwo: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
  }),
  shippingAddressFields: PropTypes.shape({
    addressOne: PropTypes.string,
    addressTwo: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
  }),
  billingAddressSettings: PropTypes.shape({
    addressOne: PropTypes.bool,
    addressTwo: PropTypes.bool,
    city: PropTypes.bool,
    state: PropTypes.bool,
    zip: PropTypes.bool,
    isZipRequired: PropTypes.bool,
    isBillingAddressRequired: PropTypes.bool,
  }),
  shippingAddressSettings: PropTypes.shape({
    addressOne: PropTypes.bool,
    addressTwo: PropTypes.bool,
    city: PropTypes.bool,
    state: PropTypes.bool,
    zip: PropTypes.bool,
    isZipRequired: PropTypes.bool,
    isShippingAddressRequired: PropTypes.bool,
  }),
  setBillingAddressFields: PropTypes.func,
  setShippingAddressFields: PropTypes.func,
  billingFormValidationRef: PropTypes.any,
  shippingFormValidationRef: PropTypes.any,
  disableDropdownExpansion: PropTypes.bool,
  fieldAlignment: PropTypes.string,
};

export default AddressFieldsDropDown;
